import { httpsCallable } from 'firebase/functions';
import { getFbFunctions } from './development';
import * as Sentry from '@sentry/browser';
import {
  doc,
  getDoc,
  getFirestore,
  // initializeFirestore,
  // persistentLocalCache,
} from 'firebase/firestore';
import { Input, Modal } from 'ant-design-vue';
import { h } from 'vue';
import { Select, message } from 'ant-design-vue';
import { hostname } from '../assets/js/helpers/DomainUtils.ts';
import { clearImageCache } from '../assets/js/imageStore';

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GithubAuthProvider,
  GoogleAuthProvider,
  signInWithRedirect,
  deleteUser,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  fetchSignInMethodsForEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  checkActionCode,
  applyActionCode,
  updateProfile,
  onAuthStateChanged,
  signInWithPopup,
  OAuthProvider,
  reauthenticateWithPopup,
  reload,
  getAuth,
  signInWithCustomToken,
  getIdTokenResult,
} from 'firebase/auth';

import type { AuthProvider, MultiFactorError, UserCredential } from 'firebase/auth';

// import {
//   initializeAppCheck,
//   ReCaptchaEnterpriseProvider,
// } from 'firebase/app-check';

import { firebaseConfig } from '../firebaseConfig';
import { initializeApp, getApp } from 'firebase/app';
// import { getPerformance } from 'firebase/performance';
import { ref } from 'vue';
import type { InvitationLoginResponse, ipifyResponse, SocialLogin } from './types';
import type { LocationQueryValue, Router } from 'vue-router';
import type { MessageApi } from 'ant-design-vue/es/message';
import { setHTMLDarkMode } from '../assets/js/helpers/DarkThemeHelper.ts';
import { getBillingPlan } from '../assets/js/helpers/BillingPlanHelper.ts';
import { usePostHog } from '../composables/usePosthog.ts';
import {
  beginInlineMfaChallenge,
  clearInlineMfaChallenge,
  submitInlineMfaCode,
} from './auth/mfa.ts';

let pendingInlineMfaCompletion: ((result: UserCredential) => Promise<any>) | null = null;
// Only suppress the watcher in the page that is actively starting SSO logout.
let ssoLogoutInProgress = false;

const translateError = (code: string) => {
  switch (code) {
    case 'auth/invalid-email':
      return 'Incorrect username or password.';
    case 'auth/invalid-password':
      return 'Incorrect username or password.';
    case 'auth/insufficient-permission':
      return 'Incorrect username or password.';
    case 'auth/invalid-credential':
      return 'Incorrect username or password.';
    case 'auth/user-not-found':
      return 'Incorrect username or password.';
    case 'auth/wrong-password':
      return 'Incorrect username or password.';
    case 'auth/web-storage-unsupported':
      return 'This browser is not supported or 3rd party cookies may be disabled.';
    case 'auth/popups-blocked':
      return 'Popups are blocked. Please enable popups to login.';
    case 'auth/cancelled-popup-request':
      return 'Login request was cancelled.';
    case 'auth/account-exists-with-different-credential':
      return 'This email is already in use. Please login with the correct provider.';
    case 'Email not verified. Please check your email for a verification code or contact support.':
      return 'Email not verified. Please check your email for a verification code or contact support.';
    case 'Email not verified. Please verify before logging in.':
      return 'Email not verified. Please verify before logging in.';
    case 'Domain not authorized':
      return 'You are not authorized to login to this domain. Please contact your team administrator.';
    case 'Unable to send verification email. Please try again later.':
      return 'Unable to send verification email. Please try again later.';
    default:
      return 'An error has occurred. Please try again.';
  }
};

async function finalizeEmailLogin(this: any, user: UserCredential['user']) {
  this.lastUsedAuthMethod = 'email';
  this.user = user.toJSON();
  await this.getUserTeams();

  if (this.checkedDomain?.data?.tid) await this.getTeam(this.checkedDomain.data.tid);
  await this.getAuthorizedDomains();

  const { minimatch } = await import('minimatch');
  if (this.curDomain && minimatch(this.curDomain, this.authorizedDomains)) {
    await this.getSearchKey(user.uid);
    await this.getUserPreferences(user.uid);

    if (this.checkedDomain?.data?.tid) await this.switchTeam(this.checkedDomain.data.tid);

    return {
      success: true,
    };
  }

  this.user = null;
  this.team = null;
  this.searchKey = null;

  return {
    success: false,
    message: 'Domain not authorized',
  };
}

function createMfaRequiredResult() {
  return {
    success: false,
    requiresMfa: true,
    message: 'auth/multi-factor-auth-required',
  };
}

function beginAuthPageMfa(
  this: any,
  error: MultiFactorError,
  onSuccess: (result: UserCredential) => Promise<any>,
) {
  beginInlineMfaChallenge.call(this, error);
  pendingInlineMfaCompletion = onSuccess;
  return createMfaRequiredResult();
}

async function finalizeInvitationLogin(
  this: any,
  user: UserCredential['user'],
  token: string,
) {
  const functions = getFbFunctions(getApp(this.firebaseApp));
  const { minimatch } = await import('minimatch');

  this.lastUsedAuthMethod = 'email';
  const acceptInvitation = httpsCallable(
    functions,
    'teams-acceptInvitationLogin',
  );
  const data = {
    token,
    origin: this.curDomain,
  };
  const invitationResult = await acceptInvitation(data) as InvitationLoginResponse;

  if (!invitationResult.data) {
    const authorizedDomains = await this.getAuthorizedDomains();
    if (!minimatch(hostname(), authorizedDomains)) {
      await this.logout();
    }
    return { success: false, message: 'Invitation failed' };
  }

  await reload(user);
  this.user = user.toJSON();
  await this.getUserTeams();
  await this.getTeam(invitationResult.data.team);
  const authorizedDomains = await this.getAuthorizedDomains();


  if (!invitationResult.data.success) {
    if (!minimatch(hostname(), authorizedDomains)) {
      await this.logout();
    }
    return { success: false, message: 'Already in team' };
  }

  if (!this.curDomain || !minimatch(this.curDomain, authorizedDomains)) {
    this.user = null;
    this.team = null;
    this.searchKey = null;
    await this.logout();
    return { success: false, message: 'Domain not authorized' };
  }

  await this.getSearchKey(user.uid);
  await this.getUserPreferences(user.uid);
  await this.switchTeam(invitationResult.data.team);

  if (!minimatch(hostname(), authorizedDomains)) {
    await this.logout();
    return { success: false, message: 'Domain not authorized' };
  }

  return { success: true };
}

async function finalizeProviderLogin(
  this: any,
  {
    result,
    message,
    redirect,
    router,
    providerId,
    hint,
    token,
  }: {
    result: UserCredential,
    message: MessageApi,
    redirect: LocationQueryValue | LocationQueryValue[] | undefined,
    router: Router,
    providerId: string,
    hint?: string | null,
    token?: string | null,
  },
) {
  const functions = getFbFunctions(getApp(this.firebaseApp));
  this.lastUsedAuthMethod = providerId as 'email' | 'google.com' | 'github.com' | 'microsoft.com' | 'sso';
  const { minimatch } = await import('minimatch');

  if (
    this.purchaseFlow == 'basic-yearly' ||
    this.purchaseFlow == 'basic-monthly' ||
    this.purchaseFlow == 'plus-yearly' ||
    this.purchaseFlow == 'plus-monthly'
  ) {
    const authorizedDomains = await this.getAuthorizedDomains();
    await this.getSearchKey(result.user.uid);
    await this.getUserPreferences(result.user.uid);

    if (minimatch(hostname(), authorizedDomains)) {
      message.loading('Loading Checkout...', 0);
      const price = getBillingPlan(
        this.purchaseFlow.split('-')[0],
        this.purchaseFlow.split('-')[1],
      );
      this.checkout({ price });
    } else {
      await this.logout();
      message.error({
        content: translateError('Domain not authorized'),
        key: 'login',
        duration: 2.5,
      });
    }

    return result;
  }

  const email = result.user.email ?? result.user.providerData?.find((p: { email?: string | null }) => p?.email !== null)?.email ?? '';
  if (token && hint && hint == email) {
    message.loading('Logging in...', 0);
    const acceptInvitation = httpsCallable(
      functions,
      'teams-acceptInvitationLogin',
    );
    const data = {
      token,
      origin: this.curDomain,
    };
    const invitationResult = await acceptInvitation(data) as InvitationLoginResponse;
    if (invitationResult.data) {
      await reload(result.user);
      this.user = result.user.toJSON();
      await this.getUserTeams();
      await this.getTeam(invitationResult.data.team);
      const authorizedDomains = await this.getAuthorizedDomains();
      if (invitationResult.data.success) {
        if (minimatch(this.curDomain, authorizedDomains)) {
          await this.getSearchKey(result.user.uid);
          await this.getUserPreferences(result.user.uid);
          await this.switchTeam(invitationResult.data.team);

          if (minimatch(hostname(), authorizedDomains)) {
            message.destroy();
            message.success({
              content: 'Successfully joined team!',
              key: 'login',
              duration: 4.5,
            });

            if (redirect) {
              router.push(redirect.toString());
            } else {
              router.push('/app/dashboard');
            }
          } else {
            await this.logout();
            message.destroy();
            message.error({
              content: translateError('Domain not authorized'),
              key: 'login',
              duration: 2.5,
            });
          }
        } else {
          this.user = null;
          this.team = null;
          this.searchKey = null;

          await this.logout();
          message.destroy();
          message.error({
            content: translateError('Domain not authorized'),
            key: 'login',
            duration: 2.5,
          });
        }
      } else if (minimatch(hostname(), authorizedDomains)) {
        message.destroy();
        if (redirect) {
          router.push(redirect.toString());
        } else {
          router.push('/app/dashboard');
        }
        message.error({
          content: 'Unable to join team.',
          key: 'login',
          duration: 2.5,
        });
      } else {
        await this.logout();
        message.destroy();
        message.error({
          content: 'Unable to join team.',
          key: 'login',
          duration: 2.5,
        });
      }
    } else {
      const authorizedDomains = await this.getAuthorizedDomains();
      if (minimatch(hostname(), authorizedDomains)) {
        message.destroy();
        if (redirect) {
          router.push(redirect.toString());
        } else {
          router.push('/app/dashboard');
        }
        message.error({
          content: 'Unable to join team. Please contact your team administrator.',
          key: 'login',
          duration: 2.5,
        });
      } else {
        await this.logout();
        message.destroy();
        message.error({
          content: 'Unable to join team. Please contact your team administrator.',
          key: 'login',
          duration: 2.5,
        });
      }
    }

    return result;
  }

  message.loading('Logging in...', 0);
  this.user = result.user.toJSON();
  await this.getUserTeams();
  const tokenResult = await result.user.getIdTokenResult();
  /* @ts-expect-error sign_in_attributes */
  const tokenTenantId = tokenResult?.claims.firebase?.sign_in_attributes?.tenant?.id ?? null;
  if (this.checkedDomain?.data?.tid) await this.getTeam(this.checkedDomain.data.tid);
  else if (tokenTenantId) await this.getTeam(tokenTenantId);
  const authorizedDomains = await this.getAuthorizedDomains();
  await this.getSearchKey(result.user.uid);
  await this.getUserPreferences(result.user.uid);

  if (minimatch(hostname(), authorizedDomains)) {
    if (this.checkedDomain?.data?.tid) await this.switchTeam(this.checkedDomain.data.tid);

    if (redirect) {
      message.destroy();
      router.push(redirect.toString());
    } else {
      message.destroy();
      router.push('/app/dashboard');
    }
  } else {
    await this.logout();
    message.destroy();
    message.error({
      content: translateError('Domain not authorized'),
      key: 'login',
      duration: 2.5,
    });
  }

  return result;
}

export async function completeInlineMfaSignIn({ code }: { code: string }) {
  if (!pendingInlineMfaCompletion) {
    return {
      success: false,
      message: 'MFA session expired',
    };
  }

  const finalize = pendingInlineMfaCompletion;

  try {
    const result = await submitInlineMfaCode.call(this, code);
    pendingInlineMfaCompletion = null;
    return await finalize(result);
  } catch (error: any) {
    if (error?.message === 'invalid code' || error?.message === 'sms code not sent') {
      return {
        success: false,
        requiresMfa: true,
        message: 'auth/multi-factor-auth-required',
      };
    }

    return {
      success: false,
      message: error?.code ?? error?.message ?? 'auth/unknown-error',
    };
  }
}

export function cancelInlineMfaSignIn() {
  pendingInlineMfaCompletion = null;
  clearInlineMfaChallenge();
}

export function setInlineMfaCompletionHandler(
  handler: ((result: UserCredential) => Promise<any>) | null,
) {
  pendingInlineMfaCompletion = handler;
}

const googleProvider = (new GoogleAuthProvider().setCustomParameters({
  prompt: 'select_account',
}) as GoogleAuthProvider);
const githubProvider = (new GithubAuthProvider().setCustomParameters({
  prompt: 'select_account',
}) as GithubAuthProvider);
const microsoftProvider = (new OAuthProvider('microsoft.com').setCustomParameters({
  prompt: 'select_account',
  tenant: 'common',
}) as OAuthProvider);
const ssoProvider = (new OAuthProvider('oidc.rackmanage').setCustomParameters({
  prompt: 'login',
}) as OAuthProvider);

export async function internalAuthTrigger(
  message: MessageApi,
  redirect: LocationQueryValue | LocationQueryValue[] | undefined,
  router: Router,
  provider: AuthProvider,
  hint: string,
  token: LocationQueryValue | LocationQueryValue[] | undefined,
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const normalizedRedirect = Array.isArray(redirect) ? redirect[0] : redirect;
  const normalizedToken = Array.isArray(token) ? token[0] ?? null : token ?? null;

  return signInWithPopup(auth, provider)
    .then(async (result) => {
      if (result != null && result.user) {
        return await finalizeProviderLogin.call(this, {
          result,
          message,
          redirect: normalizedRedirect,
          router,
          providerId: provider.providerId,
          hint,
          token: normalizedToken,
        });
      }
    })
    .catch(async (err) => {
      console.warn(err);
      if (err.code == 'auth/multi-factor-auth-required') {
        return beginAuthPageMfa.call(
          this,
          err,
          async (result: UserCredential) => {
            await finalizeProviderLogin.call(this, {
              result,
              message,
              redirect: normalizedRedirect,
              router,
              providerId: provider.providerId,
              hint,
              token: normalizedToken,
            });

            return {
              success: true,
              handled: true,
            };
          },
        );
      }
      else {
        message.error({ content: translateError(err.code), key: 'login', duration: 2.5 });
      }
    });
}

/** Returns true if the current user's Firestore doc has a root-level `team` field (legacy single-team). */
export async function userDocHasLegacyTeam(): Promise<boolean> {
  if (!this.user?.uid) return false;
  const fbApp = getApp(this.firebaseApp);
  const docRef = doc(getFirestore(fbApp, this.dbid), 'users', this.user.uid);
  const snap = await getDoc(docRef);
  return snap.data()?.team != null;
}

/** One read: legacy team flag + whether team switcher tour was already shown. */
export async function getUserDocTeamSwitcherTourFlags(): Promise<{ hasLegacyTeam: boolean, tourShown: boolean }> {
  const out = { hasLegacyTeam: false, tourShown: false };
  if (!this.user?.uid) return out;
  const fbApp = getApp(this.firebaseApp);
  const docRef = doc(getFirestore(fbApp, this.dbid), 'users', this.user.uid);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return out;
  const data = snap.data();
  out.hasLegacyTeam = data?.team != null;
  out.tourShown = data?.preferences?.teamSwitcherTourShown === true;
  return out;
}

export async function authState() {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const { posthog } = usePostHog();

  onAuthStateChanged(auth, async (user) => {
    if (user) {
      this.user = user.toJSON();
      await this.getUserTeams();
      await this.getSearchKey(user.uid);

      let subscription = 'Free';
      let internal = false;

      try {
        const decodedToken = await getIdTokenResult(user, true);
        switch (decodedToken?.claims?.stripeRole) {
          case 'basic':
            subscription = 'Basic';
            break;
          case 'plus':
            subscription = 'Plus';
            break;
        }
        this.system.permissions = decodedToken?.claims?.system_permissions as string[] || [];
        if (decodedToken?.claims?.system_permissions) {
          internal = true;
        }
      } catch {
        this.system.permissions = [];
      }

      if (this.userMode == 'team' && !this.team) {
        this.userMode = 'user';
      }

      // Identify user in Sentry
      Sentry.setUser({
        email: user?.email ?? user?.providerData?.find(p => p?.email)?.email ?? undefined,
        id: user?.uid || undefined,
        username: user?.displayName || undefined,
      });

      // Identify user in PostHog
      if (posthog) {
        posthog?.identify(user.uid, {
          email: user?.email ?? user.providerData?.find(p => p?.email)?.email ?? '',
          name: user.displayName,
          uid: user.uid,
          subscription,
          internal,
        });
      }
    } else {
      this.user = null;
      this.team = null;
      this.teamData = null;
      this.userMode = 'user';
      this.userTeams = [];
      this.searchKey = null;

      if (!ssoLogoutInProgress && window.location.pathname.startsWith('/app')) {
        window.location.href = '/auth/login?redirect=' + window.location.pathname;
      }

      Sentry.setUser(null);
      posthog?.reset();
    }
  });
}

export async function signup({ email, password }: { email: string, password: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  const response = await createUserWithEmailAndPassword(auth, email, password);
  if (response && response.user) {
    this.lastUsedAuthMethod = 'email';
    if (
      this.purchaseFlow == 'basic-yearly' ||
      this.purchaseFlow == 'basic-monthly' ||
      this.purchaseFlow == 'plus-yearly' ||
      this.purchaseFlow == 'plus-monthly'
    ) {
      const redirectURL = this.buildRedirectURL();
      const changeLink = httpsCallable(functions, 'links-verifyEmail');
      const data = {
        email: email,
        url: redirectURL,
        template: 'signup-verification',
      };
      const linkResult = await changeLink(data);
      if (linkResult.data == false || linkResult.data == 'false') {
        throw new Error('verification email failed');
      }
    } else {
      const changeLink = httpsCallable(functions, 'links-verifyEmail');
      const data = {
        email: email,
        template: 'signup-verification',
      };
      const linkResult = await changeLink(data);
      if (linkResult.data == false || linkResult.data == 'false') {
        throw new Error('verification email failed');
      }
    }
  } else {
    throw new Error('signup failed');
  }
}

export async function signupWithInvitation(
  { email, password, token }: { email: string, password: string, token: string },
): Promise<{ success: boolean, message?: string }> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  const response = await createUserWithEmailAndPassword(auth, email, password);
  if (response && response.user) {
    this.lastUsedAuthMethod = 'email';
    const acceptInvitation = httpsCallable(functions, 'teams-acceptInvitation');
    const data = {
      token: token,
      origin: this.curDomain,
    };
    const invitationResult = await acceptInvitation(data) as { data: { success: boolean, team: string } | false };
    if (invitationResult.data != false) {
      await reload(response.user);
      this.user = response.user.toJSON();
      await this.getUserTeams();
      await this.getTeam(invitationResult.data.team);
      await this.getAuthorizedDomains();

      const { minimatch } = await import('minimatch');
      if (this.curDomain && minimatch(this.curDomain, this.authorizedDomains)) {
        await this.getSearchKey(response.user.uid);
        await this.getUserPreferences(response.user.uid);
        await this.switchTeam(invitationResult.data.team);

        return {
          success: true,
        };
      } else {
        this.user = null;
        this.team = null;
        this.searchKey = null;

        return {
          success: false,
          message: 'Domain not authorized',
        };
      }
    } else {
      await reload(response.user);
      this.user = response.user.toJSON();
      return {
        success: false,
        message: 'Invitation failed',
      };
    }
  } else {
    return {
      success: false,
      message: 'Signup failed',
    };
  }
}

export async function login({ email, password }: { email: string, password: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  try {
    const response = await signInWithEmailAndPassword(auth, email, password);

    if (response && response.user && response.user.emailVerified) {
      return await finalizeEmailLogin.call(this, response.user);
    } else if (response && response.user && !response.user.emailVerified) {
      this.lastUsedAuthMethod = 'email';
      const changeLink = httpsCallable(functions, 'links-verifyEmail');
      if (
        this.purchaseFlow == 'basic-yearly' ||
        this.purchaseFlow == 'basic-monthly' ||
        this.purchaseFlow == 'plus-yearly' ||
        this.purchaseFlow == 'plus-monthly'
      ) {
        const redirectURL = this.buildRedirectURL();
        const data = {
          email: email,
          url: redirectURL,
        };

        const linkResult = await changeLink(data);
        if (linkResult.data == false || linkResult.data == 'false') {
          return {
            success: false,
            message:
              'Unable to send verification email. Please try again later.',
          };
        } else {
          return {
            success: false,
            message:
              'Email not verified. Please check your email for a verification code or contact support.',
          };
        }
      } else {
        const data = {
          email: email,
        };
        const linkResult = await changeLink(data);
        if (linkResult.data == false || linkResult.data == 'false') {
          return {
            success: false,
            message:
              'Unable to send verification email. Please try again later.',
          };
        } else {
          return {
            success: false,
            message:
              'Email not verified. Please check your email for a verification code or contact support.',
          };
        }
      }
    } else {
      return {
        success: false,
        message: 'Login failed',
      };
    }
  } catch (error: any) {
    if (error.code) {
      if (error.code == 'auth/multi-factor-auth-required') {
        return beginAuthPageMfa.call(
          this,
          error,
          async (result: UserCredential) => await finalizeEmailLogin.call(this, result.user),
        );
      } else {
        return {
          success: false,
          message: error.code,
        };
      }
    } else {
      return {
        success: false,
        message: error,
      };
    }
  }
}

export async function loginWithInvitation(
  { email, password, token }: { email: string, password: string, token: string },
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  try {
    const response = await signInWithEmailAndPassword(auth, email, password);
    if (response && response.user && token) {
      return await finalizeInvitationLogin.call(this, response.user, token);
    } else if (response && response.user) {
      const { minimatch } = await import('minimatch');
      this.lastUsedAuthMethod = 'email';
      this.user = response.user.toJSON();
      await this.getUserTeams();
      await this.getAuthorizedDomains();

      if (minimatch(this.curDomain, this.authorizedDomains)) {
        await this.getSearchKey(response.user.uid);
        await this.getUserPreferences(response.user.uid);

        return {
          success: false,
          message: 'Invitation failed',
        };
      } else {
        this.user = null;
        this.team = null;
        this.searchKey = null;

        return {
          success: false,
          message: 'Domain not authorized',
        };
      }
    } else {
      return {
        success: false,
        message: 'Login failed',
      };
    }
  } catch (error: any) {
    if (error.code) {
      if (error.code == 'auth/multi-factor-auth-required') {
        return beginAuthPageMfa.call(
          this,
          error,
          async (result: UserCredential) => await finalizeInvitationLogin.call(this, result.user, token),
        );
      } else {
        return {
          success: false,
          message: error.code,
        };
      }
    } else {
      return {
        success: false,
        message: error,
      };
    }
  }
}

export async function acceptInvitation(
  { token }: { token: string },
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);
  const currentUser = auth.currentUser;

  if (currentUser) {
    try {
      const { minimatch } = await import('minimatch');
      const acceptInvitation = httpsCallable(
        functions,
        'teams-acceptInvitationLogin',
      );
      const data = {
        token: token,
        origin: this.curDomain,
      };
      const invitationResult = await acceptInvitation(data) as InvitationLoginResponse;
      if (invitationResult.data) {
        await reload(currentUser);
        await this.getUserTeams();
        await this.getTeam(invitationResult.data.team);
        const authorizedDomains = await this.getAuthorizedDomains();
        if (invitationResult.data.success) {
          if (this.curDomain && minimatch(this.curDomain, authorizedDomains)) {
            await this.getSearchKey(currentUser.uid);
            await this.getUserPreferences(currentUser.uid);
            await this.switchTeam(invitationResult.data.team);

            if (minimatch(hostname(), authorizedDomains)) {
              return {
                success: true,
              };
            } else {
              this.logout();
              return {
                success: false,
                message: 'Domain not authorized',
              };
            }
          } else {
            this.user = null;
            this.team = null;
            this.searchKey = null;

            await this.logout();
            return {
              success: false,
              message: 'Domain not authorized',
            };
          }
        } else {
          if (minimatch(hostname(), authorizedDomains)) {
            return {
              success: false,
              message: 'Already in team',
            };
          } else {
            await this.logout();
            return {
              success: false,
              message: 'Already in team',
            };
          }
        }
      } else {
        const authorizedDomains = await this.getAuthorizedDomains();
        if (minimatch(hostname(), authorizedDomains)) {
          return {
            success: false,
            message: 'Invitation failed',
          };
        } else {
          await this.logout();
          return {
            success: false,
            message: 'Invitation failed',
          };
        }
      }
    } catch (error: any) {
      if (error.code) {
        if (error.code == 'auth/multi-factor-auth-required') {
          return beginAuthPageMfa.call(
            this,
            error,
            async () => {
              return await this.acceptInvitation({ token });
            },
          );
        } else {
          return {
            success: false,
            message: error.code,
          };
        }
      } else {
        return {
          success: false,
          message: error,
        };
      }
    }
  } else {
    return {
      success: false,
      message: 'You are not logged in. Please log in to accept the invitation.',
    };
  }
}

export async function loginWithToken({ token }: { token: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  try {
    const response = await signInWithCustomToken(auth, token);
    if (response && response.user) {
      this.user = response.user.toJSON();
      await this.getUserTeams();
      await this.getAuthorizedDomains();

      const { minimatch } = await import('minimatch');
      if (this.curDomain && minimatch(this.curDomain, this.authorizedDomains)) {
        await this.getSearchKey(response.user.uid);
        await this.getUserPreferences(response.user.uid);

        return {
          success: true,
        };
      } else {
        this.user = null;
        this.team = null;
        this.searchKey = null;

        return {
          success: false,
          message: 'Domain not authorized',
        };
      }
    } else {
      return {
        success: false,
        message: 'Login failed',
      };
    }
  } catch (error: any) {
    if (error.code) {
      return {
        success: false,
        message: error.code,
      };
    } else {
      return {
        success: false,
        message: error,
      };
    }
  }
}

export async function signInWithGoogle(
  { message, redirect, router, hint, token, mode } : SocialLogin,
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (hint) {
    googleProvider.setCustomParameters({
      login_hint: hint,
      prompt: 'select_account',
    });
  }

  if (mode == 'redirect') {
    await signInWithRedirect(auth, googleProvider);
  } else {
    return await this.internalAuthTrigger(
      message,
      redirect,
      router,
      googleProvider,
      hint,
      token,
    );
  }
}

export async function signInWithGithub(
  { message, redirect, router, hint, token, mode } : SocialLogin,
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (hint) {
    githubProvider.setCustomParameters({
      login: hint,
      prompt: 'select_account',
    });
  }

  if (mode == 'redirect') {
    await signInWithRedirect(auth, githubProvider);
  } else {
    return await this.internalAuthTrigger(
      message,
      redirect,
      router,
      githubProvider,
      hint,
      token,
    );
  }
}

export async function signInWithMicrosoft(
  { message, redirect, router, hint, token, mode } : SocialLogin,
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (hint) {
    microsoftProvider.setCustomParameters({
      login_hint: hint,
      prompt: 'select_account',
      tenant: 'common',
    });
  }
  if (mode == 'redirect') {
    await signInWithRedirect(auth, microsoftProvider);
  } else {
    return await this.internalAuthTrigger(
      message,
      redirect,
      router,
      microsoftProvider,
      hint,
      token,
    );
  }
}

export async function signInWithSSO(
  { message, redirect, router, hint, idp, token, mode } : SocialLogin,
  skipPostSSOLogin = false,
) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  try {
    if (hint) {
      ssoProvider.setCustomParameters({
        kc_idp_hint: idp ?? '',
        login_hint: hint,
        prompt: 'login',
      });
    }

    if (mode == 'redirect') {
      await signInWithRedirect(auth, ssoProvider);
    } else {
      const result = await this.internalAuthTrigger(
        message,
        redirect,
        router,
        ssoProvider,
        hint,
        token,
      );

      if ((result as UserCredential).user && !skipPostSSOLogin) {
        await this.postSSOLogin(router, redirect);
      }

      return result;
    }
  } catch (error: any) {
    console.warn(error);
    message.error({
      content: translateError(error.code),
      key: 'login',
      duration: 2.5,
    });
  }
}

export async function resetPassword({ email }: { email: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  // get sign in methods for email
  const signInMethods = await fetchSignInMethodsForEmail(auth, email);
  // if email registered with password, send password reset email
  if (signInMethods.length > 0 && signInMethods.includes('password')) {
    const resetLink = httpsCallable(functions, 'links-reset');
    const data = {
      email: email,
    };
    resetLink(data)
      .then(async (result) => {
        if (result.data === false) {
          throw new Error('Error sending password reset email.');
        }
      })
      .catch(async () => {
        throw new Error('Error sending password reset email.');
      });
  }
}

export async function verifyOobCode({ code }: { code: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  try {
    const email = await verifyPasswordResetCode(auth, code);
    return email;
  }
  catch {
    return false;
  }
}

export async function resetPasswordStep2({ code, password, email }: { code: string, password: string, email: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  const newPassword = password;

  confirmPasswordReset(auth, code, newPassword)
    .then(async () => {
      const response = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      if (response && response.user) {
        this.user = response.user.toJSON();
        await this.getUserTeams();
      }
      const resetLink = httpsCallable(functions, 'links-resetStep2');
      const { UAParser } = await import('@ua-parser-js/pro-business');
      const ua = new UAParser(navigator.userAgent);
      const device = ua.getResult();
      const ipify = await fetch('https://api.ipify.org?format=json');
      const ip = (await ipify.json() as ipifyResponse)?.ip || 'Unknown';

      const data = {
        email: email,
        device: device.browser.name + ' on ' + device.os.name,
        ip: ip,
      };
      resetLink(data)
        .then(async (result) => {
          if (result.data === false) {
            throw new Error('An error occurred. Please try again.');
          }
        })
        .catch(async () => {
          throw new Error('An error occurred. Please try again.');
        });
    })
    .catch(async (err) => {
      if (err.code == 'auth/multi-factor-auth-required') {
        await this.verifyMFA(err);
      } else {
        throw new Error(
          'Error resetting password. Invalid link or password too weak.',
        );
      }
    });
}

const SSO_LOGOUT_BASE_URL = import.meta.env.VITE_SSO_LOGOUT_BASE_URL ?? 'https://sso.rackmanage.io';

export async function isSSOUser(): Promise<boolean> {
  const auth = getAuth(getApp(this.firebaseApp));
  if (!auth.currentUser) return false;
  const idToken = await getIdTokenResult(auth.currentUser, true);
  if (idToken?.signInProvider !== 'oidc.rackmanage') return false;
  return true;
}

export async function initiateSSOLogout(): Promise<void> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const user = auth.currentUser;
  if (!user) return;

  const realm = import.meta.env.VITE_SSO_REALM ?? 'r';

  const state = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const postLogoutRedirectUri = `${window.location.origin}/auth/sso-logout`;
  const logoutUrl = new URL(
    `${SSO_LOGOUT_BASE_URL}/auth/realms/${encodeURIComponent(realm)}/protocol/openid-connect/logout`,
  );
  logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri);
  logoutUrl.searchParams.set('state', state);
  logoutUrl.searchParams.set('client_id', import.meta.env.VITE_SSO_CLIENT_ID ?? 'rackmanage');

  ssoLogoutInProgress = true;
  this.ssoLogoutState = state;
  try {
    await this.logout({ preserveSSOLogoutState: true });
    window.location.href = logoutUrl.toString();
  } catch (error) {
    ssoLogoutInProgress = false;
    this.ssoLogoutState = null;
    throw error;
  }
}

export async function logout(options?: { preserveSSOLogoutState?: boolean }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  clearImageCache();

  this.user = null;
  this.team = null;
  this.searchKey = null;
  this.system.permissions = null;
  if (!options?.preserveSSOLogoutState) this.ssoLogoutState = null;

  if (auth) {
    try {
      await signOut(auth);
    } catch (error: any) {
      if (options?.preserveSSOLogoutState) throw error;
      console.warn(error);
    }
  }

  setHTMLDarkMode(false);
}

export async function deleteAccount({ password }: { password: string }) {
  const ownedTeams = await this.getUserOwnedTeams();

  if (ownedTeams.length > 0) {
    throw new Error('You must delete your team or transfer ownership before deleting your account.');
  }

  // If user is using SSO or github / google, give login popup to delete account
  if (this.user && this.user.providerData[0].providerId !== 'password') {
    let provider;
    if (this.user.providerData[0].providerId == 'github.com') {
      provider = new GithubAuthProvider();
    } else if (this.user.providerData[0].providerId == 'google.com') {
      provider = new GoogleAuthProvider();
    } else if (this.user.providerData[0].providerId == 'microsoft.com') {
      provider = new OAuthProvider('microsoft.com');
    } else {
      provider = ssoProvider;
    }
    // Reauthenticate with SSO provider
    try {
      const auth = getAuth(getApp(this.firebaseApp));
      if (!auth.currentUser) return;
      await reauthenticateWithPopup(auth.currentUser, provider);
    } catch (e: any) {
      if (e.code == 'auth/multi-factor-auth-required') {
        await this.verifyMFA(e);
      } else {
        throw new Error(e);
      }
    }
  } else if (this.user && this.user.email) {
    // Reauthenticate with password
    try {
      const credential = EmailAuthProvider.credential(
        this.user.email,
        password,
      );
      const auth = getAuth(getApp(this.firebaseApp));
      if (!auth.currentUser) return;
      await reauthenticateWithCredential(auth.currentUser, credential);
    } catch (e: any) {
      if (e.code == 'auth/multi-factor-auth-required') {
        await this.verifyMFA(e);
      } else {
        throw new Error(e);
      }
    }
  } else {
    throw new Error('Unable to reauthenticate user.');
  }

  // If user is a member of any teams, remove them from the teams
  if (this.userTeams.length > 0) {
    for (const team of this.userTeams) {
      if (!(await this.deleteTeamMember(this.user.uid, team.id))) {
        throw new Error(`Unable to leave team ${team.name}.`);
      }
    }
  }

  const auth = getAuth(getApp(this.firebaseApp));
  if (!auth.currentUser) return;
  await deleteUser(auth.currentUser)
    .then(() => {
      this.user = null;
      this.team = null;
      this.searchKey = null;
    })
    .catch((error) => {
      console.warn(error);
      throw new Error(error);
    });
}

export async function changePassword({ oldPassword, newPassword }: { oldPassword: string, newPassword: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  if (!auth.currentUser || !auth.currentUser.email) {
    throw new Error('Unable to change password. Please log in again.');
  }

  try {
    const credential = EmailAuthProvider.credential(
      auth.currentUser.email,
      oldPassword,
    );
    const result = await reauthenticateWithCredential(
      auth.currentUser,
      credential,
    );
    this.user = result.user.toJSON();
  } catch (err: any) {
    if (err.code == 'auth/multi-factor-auth-required') {
      await this.verifyMFA(err);
    } else {
      throw new Error('Incorrect old password. Please try again.');
    }
  }
  updatePassword(auth.currentUser, newPassword)
    .then(async () => {
      if (!auth.currentUser || !auth.currentUser.email) {
        throw new Error('Unable to change password. Please log in again.');
      }

      const { UAParser } = await import('@ua-parser-js/pro-business');
      const ua = new UAParser(navigator.userAgent);
      const device = ua.getResult();
      const ipify = await fetch('https://api.ipify.org?format=json');
      const ip = (await ipify.json() as ipifyResponse)?.ip || 'Unknown';

      const resetLink = httpsCallable(functions, 'links-reset');
      const data = {
        email: auth.currentUser.email,
        template: 'password-change',
        device: device.browser.name + ' on ' + device.os.name,
        ip: ip,
      };
      resetLink(data);
    })
    .catch((error) => {
      throw new Error(error);
    });
}

export async function changeEmail({ password, newEmail }: { password: string, newEmail: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  if (!auth.currentUser || !auth.currentUser.email) {
    throw new Error('Unable to change email. Please log in again.');
  }

  try {
    const credential = EmailAuthProvider.credential(
      auth.currentUser.email,
      password,
    );
    const result = await reauthenticateWithCredential(
      auth.currentUser,
      credential,
    );
    this.user = result.user.toJSON();
  } catch (err: any) {
    if (err.code == 'auth/multi-factor-auth-required') {
      await this.verifyMFA(err);
    } else {
      throw new Error('Incorrect password. Please try again.');
    }
  }

  if (!auth.currentUser || !auth.currentUser.email) {
    throw new Error('Unable to change email. Please log in again.');
  }

  const { UAParser } = await import('@ua-parser-js/pro-business');
  const ua = new UAParser(navigator.userAgent);
  const device = ua.getResult();
  const ipify = await fetch('https://api.ipify.org?format=json');
  const ip = (await ipify.json() as ipifyResponse)?.ip || 'Unknown';
  const changeLink = httpsCallable(functions, 'links-changeEmail');
  const data = {
    oldEmail: auth.currentUser.email,
    newEmail: newEmail,
    device: device.browser.name + ' on ' + device.os.name,
    ip: ip,
  };
  changeLink(data)
    .then((result) => {
      if (result.data === false) {
        throw new Error('Unable to change email. Please try again.');
      }
    })
    .catch(async (error) => {
      throw new Error(error);
    });
}

export async function changeEmailStep2({ code }: { code: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  checkActionCode(auth, code)
    .then(async () => {
      await applyActionCode(auth, code);

      // If signed in, kill session
      if (this.user) {
        await signOut(auth);
        this.user = null;
        this.team = null;
        this.searchKey = null;
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
}

export async function revertEmailChange({ code }: { code: string }): Promise<string | null | undefined> {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  try {
    const info = await checkActionCode(auth, code);
    await applyActionCode(auth, code);
    if (this.user) {
      await signOut(auth);
      this.user = null;
      this.team = null;
      this.searchKey = null;
    }
    return info.data.email;
  } catch {
    throw new Error(
      'Invalid or expired code. Please try again or contact support.',
    );
  }
}

export async function changeName(name: string) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!auth.currentUser) {
    throw new Error('Unable to change name. Please log in again.');
  }

  try {
    await updateProfile(auth.currentUser, {
      displayName: name,
    });
    // refresh user
    const user = auth.currentUser;
    this.user = user.toJSON();
  } catch (error: any) {
    throw new Error(error);
  }
}

export async function verifyEmail({ code, token }: { code: string; token?: string }) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);
  const functions = getFbFunctions(fbApp);

  let info;
  try {
    info = await checkActionCode(auth, code);
  } catch {
    throw new Error(
      'Invalid or expired code. Please try again or contact support.',
    );
  }
  try {
    await applyActionCode(auth, code);

    // If login token is provided, use that
    if (token) {
      const response = await signInWithCustomToken(auth, token);
      if (response && response.user) {
        this.user = response.user.toJSON();
      }
      // If login failed but user is signed in, revert to action code user
      else if (auth.currentUser) {
        this.user = auth.currentUser.toJSON();
      }

      await this.getUserTeams();
      await this.getSearchKey(response.user.uid);
    }
    // If no token provided, use action code user
    else if (auth.currentUser) {
      this.user = auth.currentUser.toJSON();
      await this.getUserTeams();
      await this.getSearchKey(auth.currentUser.uid);
    }
  } catch {
    if (info && info.data && info.data.email) {
      if (
        this.purchaseFlow == 'basic-yearly' ||
        this.purchaseFlow == 'basic-monthly' ||
        this.purchaseFlow == 'plus-yearly' ||
        this.purchaseFlow == 'plus-monthly'
      ) {
        const redirectURL = this.buildRedirectURL();
        const changeLink = httpsCallable(functions, 'links-verifyEmail');
        const data = {
          email: info.data.email,
          url: redirectURL,
          template: 'signup-verification',
        };

        const linkResult = await changeLink(data);
        if (linkResult.data == false || linkResult.data == 'false') {
          throw new Error(
            'Unable to verify email. Please try again or contact support.',
          );
        } else {
          throw new Error(
            'Invalid or expired code. Please check your email for a new code or contact support.',
          );
        }
      } else {
        const changeLink = httpsCallable(functions, 'links-verifyEmail');
        const data = {
          email: info.data.email,
          template: 'signup-verification',
        };
        const linkResult = await changeLink(data);
        if (linkResult.data == false || linkResult.data == 'false') {
          throw new Error(
            'Unable to verify email. Please try again or contact support.',
          );
        } else {
          throw new Error(
            'Invalid or expired code. Please check your email for a new code or contact support.',
          );
        }
      }
    } else {
      throw new Error(
        'Invalid or expired code. Please try again or contact support.',
      );
    }
  }
}

export async function completeLogin(
  result: UserCredential,
  router: Router,
  redirect: LocationQueryValue | LocationQueryValue[] | undefined,
) {
  message.loading('Logging in...', 0);
  this.user = result.user.toJSON();
  await this.getUserTeams();
  const tokenResult = await result.user.getIdTokenResult();
  /* @ts-expect-error sign_in_attributes */
  const tokenTenantId = tokenResult?.claims.firebase?.sign_in_attributes?.tenant?.id ?? null;
  if (tokenTenantId) await this.getTeam(tokenTenantId);
  const authorizedDomains = await this.getAuthorizedDomains();
  await this.getSearchKey(result.user.uid);
  await this.getUserPreferences(result.user.uid);

  const { minimatch } = await import('minimatch');
  if (minimatch(hostname(), authorizedDomains)) {
    message.destroy();
    if (redirect) {
      router.push(redirect.toString());
    } else {
      router.push('/app/dashboard');
    }
  } else {
    await this.logout();
    message.destroy();
    message.error({
      content: translateError('Domain not authorized'),
      key: 'login',
      duration: 2.5,
    });
  }
}

// Reauthenticate the current user
// This must be triggered by a user action bound to a recaptcha verifier
// The verifier must be available at window.recaptchaVerifier
export async function reauth() {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  return new Promise<void>((resolve, reject) => {
    const providers: Record<string, { enabled: boolean, id: string, provider: AuthProvider | null }> = {
      'password': { enabled: false, id: 'Password', provider: null },
      'github.com': { enabled: false, id: 'GitHub', provider: githubProvider },
      'google.com': { enabled: false, id: 'Google', provider: googleProvider },
      'microsoft.com': { enabled: false, id: 'Microsoft', provider: microsoftProvider },
      'sso': { enabled: false, id: 'Single Sign-On', provider: ssoProvider },
    };

    // Check which provider(s) the user is using
    auth.currentUser?.providerData.forEach((provider) => {
      const providerData = providers[provider.providerId];
      if (providerData) {
        providerData.enabled = true;
      } else if (provider.providerId.startsWith('oidc')) {
        const ssoProviderMap = providers['sso'];
        if (ssoProviderMap) ssoProviderMap.enabled = true;
      }
    });

    const availableProviders = Object.values(providers)
      .filter((provider) => provider.enabled)
      .map((provider) => ({
        value: provider.id,
        label: provider.id,
        provider: provider.provider || null,
      }));

    const selectedProvider = ref(availableProviders[0]?.value || '');

    Modal.confirm({
      title: 'Login Required',
      content: h(
        'div',
        {},
        {
          default: () => [
            h('p', {}, { default: () => 'This action requires you to login again.' }),
            h(Select,
              {
                style: { width: '100%' },
                onChange: (value) => { selectedProvider.value = value as string; },
                defaultValue: selectedProvider.value,
                options: availableProviders,
              },
            ),
          ],
        },
      ),
      okText: 'Continue',
      onOk: async () => {
        try {
          if (selectedProvider.value == 'Password') {
            Modal.confirm({
              title: 'Login Required',
              content: h('div', {}, {
                default: () => [
                  h('p', {}, { default: () => 'Please enter your password.' }),
                  h(Input, { placeholder: 'Password', type: 'password', id: 'reauth-password' }),
                ],
              }),
              okText: 'Submit',
              onOk: async () => {
                if (auth.currentUser && auth.currentUser.email) {
                  const password = document.querySelector<HTMLInputElement>('#reauth-password')?.value;

                  if (!password) {
                    Modal.error({
                      title: 'Error',
                      content: 'Password cannot be empty.',
                    });
                    reject(new Error('User provided an empty password'));
                  } else {
                    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
                    try {
                      const result = await reauthenticateWithCredential(auth.currentUser, credential);
                      this.user = result.user.toJSON();
                      resolve();
                    } catch {
                      Modal.error({
                        title: 'Error',
                        content: 'Incorrect password. Please try again.',
                      });
                      reject(new Error('User provided an incorrect password'));
                    }
                  }
                } else {
                  reject(new Error('No user found'));
                }
              },
            });
          } else if (selectedProvider.value == 'Single Sign-On') {
            const result = await signInWithPopup(auth, ssoProvider);
            this.user = result.user.toJSON();
            resolve();
          } else {
            const provider = availableProviders.find((p) => p.value == selectedProvider.value)?.provider;
            if (provider) {
              const result = await signInWithPopup(auth, provider);
              this.user = result.user.toJSON();
              resolve();
            } else {
              reject(new Error('No provider found'));
            }
          }
        } catch (error: any) {
          if (error.code == 'auth/multi-factor-auth-required') {
            const result = await this.verifyMFA(error);
            this.user = result.user.toJSON();
            resolve();
          } else {
            reject(error);
          }
        }
      },
      onCancel: () => {
        reject(new Error('User cancelled the password reauthentication'));
      },
    });
  });
}

export async function initializeFirebase() {
  const fbApp = initializeApp(firebaseConfig);

  // initializeFirestore(fbApp, {
  //   localCache: persistentLocalCache(),
  // });

  // Relies on ReCaptcha Enterprise which charges for > 10k requests
  // initializeAppCheck(fbApp, {
  //   provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
  //   isTokenAutoRefreshEnabled: true,
  // });

  // Performance is in beta and has a max class length of 100 characters, disabled for now
  // getPerformance(fbApp);

  return fbApp;
}

// Switch user to team and perform JIT if enabled
export async function postSSOLogin(router: Router, redirect?: string) {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  if (!auth.currentUser) return false;

  // Get Team ID from ID token
  const tokenResult = await auth.currentUser?.getIdTokenResult();
  /* @ts-expect-error sign_in_attributes */
  const tokenTenantId = tokenResult?.claims.firebase?.sign_in_attributes?.tenant?.id ?? null;

  if (!tokenTenantId) return false;

  // Check if team id matches user's team
  if (!this.userTeams.some((t: { id: string }) => t.id == tokenTenantId)) {
    await this.getTeam(tokenTenantId);

    if (this.team) {
      router.push('/app/sso');
      return true;
    } else {
      return false;
    }
  }
  // Switch user to team
  else {
    await this.switchTeam(tokenTenantId);
    if (redirect) {
      router.push(redirect);
    } else {
      router.push('/app/dashboard');
    }
    return true;
  }
}
